import { PeekButton, PeekButtonGroup, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { useNotification } from "@piiqtechnologies/ui-lib";
import { add, differenceInDays, isAfter, isBefore, sub } from "date-fns";
import React, { useCallback, useMemo, useState } from "react";
import CalendarPicker from "./CalendarPicker";
import { useIntl } from "react-intl";
import { messages } from "./messages.i18n";
const MAXIMUM_PERIOD_IN_DAYS = 90;
const EARLIEST_DAY_POSSIBLE = 365;
export const PERIODS = [7, 14, 30, 90];
const PeriodSelector = ({ onChange, selectedPeriod }) => {
    const theme = useTheme();
    const [dateChanged, setDateChanged] = useState();
    const { setNotification } = useNotification();
    const intl = useIntl();
    const numberOfDays = useMemo(() => {
        return differenceInDays(selectedPeriod.endDate, selectedPeriod.startDate) + 1;
    }, [selectedPeriod]);
    const [showCalender, setShowCalender] = useState(false);
    const toggleShowCalender = () => {
        setShowCalender((showCalendar) => !showCalendar);
    };
    const handlePeriodChange = (period) => {
        const endDate = sub(new Date(), { days: 1 });
        const startDate = sub(endDate, { days: period - 1 });
        onChange({ startDate, endDate });
        setDateChanged(undefined);
    };
    const isCustomPeriod = useMemo(() => {
        return dateChanged !== undefined;
    }, [dateChanged]);
    const handleCustomDateChanged = useCallback((date) => {
        const diffInDays = Math.max(differenceInDays(date, selectedPeriod.startDate), differenceInDays(selectedPeriod.endDate, date));
        let finalPeriod = Object.assign(Object.assign({}, selectedPeriod), { [dateChanged]: date });
        // Checks if the start date is greater than the end date and swaps them
        if (isAfter(finalPeriod.startDate, finalPeriod.endDate)) {
            finalPeriod = {
                startDate: finalPeriod.endDate,
                endDate: finalPeriod.startDate,
            };
            // Checks if the end date is less than the start date and swaps them
        }
        else if (isBefore(finalPeriod.endDate, finalPeriod.startDate)) {
            finalPeriod = {
                startDate: finalPeriod.endDate,
                endDate: finalPeriod.startDate,
            };
        }
        // If the difference in days is greater than the maximum period, adjust the period
        if (diffInDays > MAXIMUM_PERIOD_IN_DAYS) {
            if (dateChanged === "startDate") {
                finalPeriod = {
                    startDate: finalPeriod.startDate,
                    endDate: add(finalPeriod.startDate, { days: MAXIMUM_PERIOD_IN_DAYS }),
                };
            }
            else {
                finalPeriod = {
                    startDate: sub(finalPeriod.endDate, { days: MAXIMUM_PERIOD_IN_DAYS }),
                    endDate: finalPeriod.endDate,
                };
            }
            setNotification(intl.formatMessage(messages.periodLimitExceededMessage, {
                maxPeriod: MAXIMUM_PERIOD_IN_DAYS,
                dateType: dateChanged === "startDate" ? "end date" : "start date",
            }));
        }
        onChange(finalPeriod);
    }, [selectedPeriod, numberOfDays, dateChanged]);
    return (React.createElement(PeekButtonGroup, { sx: { position: "relative" } },
        PERIODS.filter((item) => typeof item === "number").map((item) => {
            return (React.createElement(PeekButton, { key: item, size: "large", onClick: () => handlePeriodChange(item), variant: numberOfDays === item && !dateChanged ? "contained" : "outlined", color: numberOfDays === item && !dateChanged ? "primary" : "info", sx: {
                    height: theme.spacing(4),
                } },
                item,
                "d"));
        }),
        React.createElement(PeekButton, { key: "custom", size: "large", variant: isCustomPeriod ? "contained" : "outlined", color: isCustomPeriod ? "primary" : "info", sx: {
                height: theme.spacing(4),
                "&:hover": {
                    backgroundColor: `${isCustomPeriod ? theme.palette.primary.main : "white"} !important`,
                    borderColor: `${theme.palette.grey[100]} !important`,
                    cursor: "default !important",
                    color: `${isCustomPeriod ? "white" : theme.palette.text.primary} !important`,
                },
            } },
            React.createElement(PeekStack, { direction: "row", spacing: 1, alignItems: "center" },
                React.createElement(CalendarPicker, { onClose: toggleShowCalender, onChange: (newDate) => handleCustomDateChanged(newDate), open: dateChanged === "startDate" && showCalender, value: selectedPeriod.startDate, minDate: sub(new Date(), { days: EARLIEST_DAY_POSSIBLE }), maxDate: sub(new Date(), { days: 1 }), onClick: () => {
                        setDateChanged("startDate");
                        toggleShowCalender();
                    } }),
                React.createElement(PeekTypography, { variant: "h3" }, "-"),
                React.createElement(CalendarPicker, { onClose: toggleShowCalender, onChange: (newDate) => handleCustomDateChanged(newDate), open: dateChanged === "endDate" && showCalender, value: selectedPeriod.endDate, minDate: sub(new Date(), { days: EARLIEST_DAY_POSSIBLE }), maxDate: sub(new Date(), { days: 1 }), onClick: () => {
                        setDateChanged("endDate");
                        toggleShowCalender();
                    } })))));
};
export default PeriodSelector;
